import { css } from "@emotion/react";
import { useState } from "react";
import { Button } from "../../Button";
import {
  CSVImportUIColumns,
  CSVImportUIRenderSearchFieldProps,
} from "../CSVImportUI";
import { CSVImportUITable } from "../CSVImportUITable";
import { ScreenLayout } from "../ScreenLayout";
import { TextField } from "../../TextField";
import { getTemplateFilename } from "../getTemplateFilename";
import { useNavigate } from "../ScreenProvider";
import { ScreenProps } from "./types";

export interface DownloadUpdateTemplateScreenProps<Row> extends ScreenProps {
  searchLabel?: string;
  onSearch?: (args: { value: string }) => Promise<{
    data: Row[];
  }>;
  renderSearchField?: (
    props: CSVImportUIRenderSearchFieldProps<Row>,
  ) => React.JSX.Element;
  headers: string[];
  csvPrefix?: string;
  onDownloadCSV?: (args: { data: Row[]; filename: string }) => void;
  defaultTemplateData?: Row[];
  columns?: CSVImportUIColumns;
  title?: string;
}

export const DownloadUpdateTemplateScreen = <
  Row extends Record<string, string>,
>({
  headers,
  searchLabel,
  onSearch,
  csvPrefix,
  onDownloadCSV,
  renderSearchField: RenderSearchField,
  defaultTemplateData = [],
  columns,
  title = "更新用CSVのダウンロード",
}: DownloadUpdateTemplateScreenProps<Row>) => {
  const { onNavigate } = useNavigate();
  const [data, setData] = useState<Row[]>(defaultTemplateData);
  const handleDownloadUpdateTemplate = () => {
    onDownloadCSV?.({
      data,
      filename: getTemplateFilename("update", csvPrefix),
    });
  };
  return (
    <ScreenLayout
      title={title}
      onNext={() => {
        onNavigate("loadCSV");
      }}
    >
      <div
        css={css`
          display: grid;
          gap: 16px;
        `}
      >
        {RenderSearchField ? (
          <RenderSearchField onChange={setData} />
        ) : onSearch ? (
          <div
            css={css`
              display: flex;
              align-items: flex-end;
              justify-content: space-between;
            `}
          >
            <form
              onSubmit={async (event) => {
                event.preventDefault();

                const formData = new FormData(event.target as HTMLFormElement);

                const { data } = await onSearch({
                  value: formData.get("value") as string,
                });

                setData(data);
              }}
            >
              <div
                css={css`
                  display: flex;
                  gap: 8px;
                  align-items: flex-end;
                `}
              >
                <TextField label={searchLabel} name="value" />

                <Button type="submit" variant="outlined">
                  検索
                </Button>
              </div>
            </form>
          </div>
        ) : null}

        <CSVImportUITable
          columns={columns}
          headers={headers}
          rows={data}
          errors={[]}
        />

        <div
          css={css`
            display: flex;
            justify-content: flex-end;
          `}
        >
          <Button
            disabled={data.length === 0}
            variant="filled"
            color="primary"
            onClick={handleDownloadUpdateTemplate}
          >
            更新用CSVをダウンロード
          </Button>
        </div>
      </div>
    </ScreenLayout>
  );
};
